import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import styles from './OrderedZoneBoard.module.scss';

const defaultGetItemId = item => (typeof item === 'object' ? item.id : item);
const noItemProps = () => ({});
const noItemClassName = () => '';
const TONE_CLASS_NAMES = {
  positive: styles.tonePositive,
  automatic: styles.toneAutomatic,
  quiet: styles.toneQuiet,
  danger: styles.toneDanger,
};

/**
 * A presentation-agnostic ordering board for collections split into semantic
 * placement zones. The caller owns data and business rules; this component
 * owns the drag surface, zone feedback, motion, and accessible DOM contract.
 */
const OrderedZoneBoard = ({
  zones,
  getItemId,
  renderItem,
  onMove,
  disabled,
  variant,
  className,
  getItemProps,
  getItemClassName,
}) => {
  const [draggingId, setDraggingId] = useState(null);
  const flowZones = zones.filter(zone => !zone.persistent);
  const firstFlowZoneId = flowZones.length ? flowZones[0].id : null;
  const lastFlowZoneId = flowZones.length ? flowZones[flowZones.length - 1].id : null;

  const handleDragEnd = (result) => {
    setDraggingId(null);
    if (!result.destination) return;
    onMove({
      itemId: result.draggableId,
      sourceZoneId: result.source.droppableId,
      sourceIndex: result.source.index,
      destinationZoneId: result.destination.droppableId,
      destinationIndex: result.destination.index,
    });
  };

  return (
    <DragDropContext
      onBeforeCapture={before => setDraggingId(before.draggableId)}
      onDragEnd={handleDragEnd}
    >
      <div
        className={`${styles.board} ${variant === 'continuous' ? styles.boardContinuous : ''} ${draggingId ? styles.boardDragging : ''} ${className}`.trim()}
        data-board-disabled={disabled || undefined}
        data-dragging-item={draggingId || undefined}
        data-board-variant={variant}
      >
        {zones.map((zone) => (
          <section
            className={`${styles.zone} ${TONE_CLASS_NAMES[zone.tone] || ''} ${zone.persistent ? styles.zonePersistent : ''} ${!zone.items.length ? styles.zoneEmpty : ''} ${zone.id === firstFlowZoneId ? styles.zoneFlowStart : ''} ${zone.id === lastFlowZoneId ? styles.zoneFlowEnd : ''}`.trim()}
            data-zone-id={zone.id}
            data-zone-persistent={zone.persistent || undefined}
            key={zone.id}
          >
            <header
              className={styles.zoneHeader}
              aria-hidden={variant === 'continuous' && !zone.persistent && !draggingId}
            >
              <span className={styles.zoneIcon} aria-hidden="true">{zone.icon}</span>
              <div className={styles.zoneCopy}>
                {zone.eyebrow && <span className={styles.zoneEyebrow}>{zone.eyebrow}</span>}
                <h2 className={styles.zoneTitle}>{zone.title}</h2>
                {zone.description && <p className={styles.zoneDescription}>{zone.description}</p>}
              </div>
              <strong className={styles.zoneCount} aria-label={zone.countLabel}>
                {zone.items.length}
              </strong>
            </header>
            <Droppable droppableId={zone.id} isDropDisabled={disabled || zone.dropDisabled}>
              {(provided, snapshot) => (
                <div
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  className={`${styles.zoneList} ${snapshot.isDraggingOver ? styles.zoneListOver : ''}`.trim()}
                  data-zone-list={zone.id}
                >
                  {zone.items.map((item, index) => {
                    const itemId = `${getItemId(item)}`;
                    const context = { item, itemId, zone, zoneId: zone.id, index };
                    return (
                      <Draggable draggableId={itemId} index={index} key={itemId} isDragDisabled={disabled}>
                        {(itemProvided, itemSnapshot) => {
                          const draggableStyle = itemProvided.draggableProps.style || {};
                          const returnStyle = itemSnapshot.isDropAnimating ? {
                            transitionDuration: '340ms',
                            transitionTimingFunction: 'cubic-bezier(.2, .78, .2, 1)',
                          } : {};
                          return (
                            <div
                              {...getItemProps(context)}
                              ref={itemProvided.innerRef}
                              {...itemProvided.draggableProps}
                              {...itemProvided.dragHandleProps}
                              style={{ ...draggableStyle, ...returnStyle }}
                              className={`${styles.item} ${itemSnapshot.isDragging ? styles.itemDragging : ''} ${itemSnapshot.isDropAnimating ? styles.itemReturning : ''} ${getItemClassName({ ...context, isDragging: itemSnapshot.isDragging })}`.trim()}
                              data-order-item-id={itemId}
                            >
                              {renderItem({ ...context, isDragging: itemSnapshot.isDragging })}
                            </div>
                          );
                        }}
                      </Draggable>
                    );
                  })}
                  {provided.placeholder}
                  {!zone.items.length && (
                    <div className={styles.empty}>{zone.emptyLabel}</div>
                  )}
                </div>
              )}
            </Droppable>
          </section>
        ))}
      </div>
    </DragDropContext>
  );
};

OrderedZoneBoard.propTypes = {
  zones: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    eyebrow: PropTypes.node,
    title: PropTypes.node.isRequired,
    description: PropTypes.node,
    icon: PropTypes.node.isRequired,
    tone: PropTypes.oneOf(['positive', 'automatic', 'quiet', 'danger']),
    items: PropTypes.arrayOf(PropTypes.any).isRequired,
    countLabel: PropTypes.string.isRequired,
    emptyLabel: PropTypes.node.isRequired,
    dropDisabled: PropTypes.bool,
    persistent: PropTypes.bool,
  })).isRequired,
  getItemId: PropTypes.func,
  renderItem: PropTypes.func.isRequired,
  onMove: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  variant: PropTypes.oneOf(['separated', 'continuous']),
  className: PropTypes.string,
  getItemProps: PropTypes.func,
  getItemClassName: PropTypes.func,
};

OrderedZoneBoard.defaultProps = {
  getItemId: defaultGetItemId,
  disabled: false,
  variant: 'separated',
  className: '',
  getItemProps: noItemProps,
  getItemClassName: noItemClassName,
};

export default OrderedZoneBoard;
